import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const { user, signIn } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => { if (user) nav("/codes", { replace: true }); }, [user, nav]);
  useEffect(() => {
    if (!cooldown) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await api.post("/auth/request-code", { email });
      setDevCode(r.data.dev_code || "");
      setStep("code"); setCode(""); setCooldown(30);
      toast.success(`Code sent to ${email}`);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const verify = async (value) => {
    const v = value ?? code;
    if (v.length !== 6) return;
    setBusy(true); setError("");
    try {
      const r = await api.post("/auth/verify", { email, code: v, client: "hub" });
      signIn(r.data.token, r.data.user);
      nav("/codes", { replace: true });
    } catch (err) { setError(errMsg(err)); setCode(""); } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="rise w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-500 text-emerald-950">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-100">Passcode Hub</h1>
            <p className="text-xs text-zinc-500">Shared verification codes for your team</p>
          </div>
        </div>

        <div className="surface p-6">
          {step === "email" ? (
            <form onSubmit={requestCode} className="flex flex-col gap-4" data-testid="login-email-form">
              <div>
                <p className="eyebrow mb-2">Sign in</p>
                <p className="text-sm text-zinc-400">Enter your team email. We'll email you a 6-digit code — no password needed.</p>
              </div>
              <Input
                type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com" data-testid="login-email-input" className="h-11 bg-[#0B0D11]"
              />
              {error && <p className="text-sm text-rose-400" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={busy || !email} data-testid="login-send-code-button" className="h-11 font-semibold">
                {busy ? "Sending…" : "Email me a code"}
              </Button>
            </form>
          ) : (
            <div className="flex flex-col gap-4" data-testid="login-code-form">
              <button onClick={() => { setStep("email"); setError(""); }} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300" data-testid="login-back-button">
                <ArrowLeft className="h-3 w-3" /> {email}
              </button>
              <div>
                <p className="eyebrow mb-2">Enter the code</p>
                <p className="text-sm text-zinc-400">Check your inbox. The code expires in 10 minutes.</p>
              </div>
              {devCode && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="dev-code-banner">
                  Dev mode · your code is <span className="font-mono font-bold tracking-widest" data-testid="dev-code-value">{devCode}</span>
                </div>
              )}
              <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} data-testid="login-code-input" autoFocus>
                <InputOTPGroup className="gap-2">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="h-12 w-11 rounded-md border bg-[#0B0D11] font-mono text-lg font-bold" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              {error && <p className="text-sm text-rose-400" data-testid="login-error">{error}</p>}
              <Button onClick={() => verify()} disabled={busy || code.length !== 6} data-testid="login-verify-button" className="h-11 font-semibold">
                {busy ? "Checking…" : "Sign in"}
              </Button>
              <button onClick={requestCode} disabled={cooldown > 0 || busy} data-testid="login-resend-button" className="text-xs text-zinc-500 hover:text-zinc-300 disabled:opacity-50">
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-zinc-600">Only emails added by your admin can sign in.</p>
      </div>
    </div>
  );
}
